import { createSelector } from '@ngrx/store';
import { Survey } from 'src/app/services/surveys.service';
import { AppState } from '..';
import { SurveyState } from '../reducers/survey.reducer';
import isPast from 'date-fns/isPast';

export const selectSurveyState = (state: AppState) => state.survey;

export const selectSurveyList = createSelector(
  selectSurveyState,
  (state: SurveyState) => state.surveys,
)

const isActive = (survey: Survey): boolean =>
  !isPast(Date.parse(survey.end_at.replace(/\s+/g, 'T')));

const isUnanswered = (survey: Survey): boolean =>
  survey.current_user_answers.length === 0;

/**
 * A multi-question survey is answered once every required question has an
 * answer; without any required question, once any question has one.
 */
export const isGroupAnswered = (group: Survey[]): boolean => {
  const required = group.filter((survey) => survey.is_required);
  if (required.length) {
    return required.every((survey) => !isUnanswered(survey));
  }
  return group.some((survey) => !isUnanswered(survey));
};

/**
 * Number of surveys still waiting for an answer from the current user.
 *
 * The store holds one entry per question. A multi-question survey (several
 * questions sharing a `survey_group_id`) counts as one pending survey while
 * it is still open and not answered, where "answered" means every required
 * question has an answer, or, when none is required, at least one question
 * has one. A standalone question counts as one survey on its own.
 */
export const selectNewSurveysCount = createSelector(
  selectSurveyState,
  (state: SurveyState) => {
    const groups = new Map<string, Survey[]>();
    let pendingStandalone = 0;

    state.surveys.forEach((survey: Survey) => {
      if (!survey.survey_group_id) {
        if (isActive(survey) && isUnanswered(survey)) {
          pendingStandalone++;
        }
        return;
      }
      const groupId = survey.survey_group_id.toString();
      groups.set(groupId, [...(groups.get(groupId) || []), survey]);
    });

    let pendingGroups = 0;
    groups.forEach((group) => {
      const open = group.some((survey) => isActive(survey) && isUnanswered(survey));
      if (open && !isGroupAnswered(group)) {
        pendingGroups++;
      }
    });

    return pendingStandalone + pendingGroups;
  }
)
